import { resolveItems } from '../../operations/getItems';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IPatch } from '../../utilities/Graft';
import { IFaceted } from '../../models/Facet';
import { mapKeys } from '../../store/reducers/keyMapReducer';
import { IInvalidateItemsPayload, invalidateItems } from '../../operations/invalidateItems';

export interface IResolveItemsPayload {
  items: {
    [itemKey: string]: IPatch<IFaceted>;
  };
}

export interface IItemResolver {
  mapKeys(payload: typeof mapKeys.payloadType): void;
  invalidateItems(payload: IInvalidateItemsPayload): void;
  resolveItems(payload: IResolveItemsPayload): void;
}

/**
 * Helper component which assists in dispatching resolveItems and mapKeys.
 * Create an instance of `ItemResolver` and invoke `resolveItems` repeatedly
 * while parsing results from a server response.
 * Item updates are carefully merged and do not accidentally override each other.
 *
 * Once done, dispatch a call to `itemResolver.execute()` to the store.
 *
 * ```ts
 * const itemResolver = new ItemResolver();
 * itemResolver.resolveItems({
 *  items: {
 *    [key1]: {
 *      ...fooFacet.pack({
 *        test1: 'a'
 *      })
 *    }
 *  }
 * });
 * itemResolver.resolveItems({
 *  items: {
 *    [key1]: { // Will not conflict.
 *      ...fooFacet.pack({
 *        test2: 'b'
 *      })
 *    }
 *  }
 * });
 * dispatch(itemResolver.execute());
 * ```
 */
export class ItemResolver {
  private _invalidateItems: IInvalidateItemsPayload['items'][];
  private _resolveItems: IResolveItemsPayload['items'][];
  private _mapKeys: (typeof mapKeys.payloadType)['mappings'][];

  private _done?: boolean;

  constructor() {
    this._invalidateItems = [];
    this._resolveItems = [];
    this._mapKeys = [];
  }

  /**
   * Declares mapping between keys. These mappings will be pushed to the cache before items are invalidated or resolved.
   */
  public mapKeys(payload: typeof mapKeys.payloadType): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    const {
      mappings
    } = payload;

    const currentPayload: (typeof mapKeys.payloadType)['mappings'] | undefined = this._mapKeys[this._mapKeys.length - 1];

    let nextPayload: (typeof mapKeys.payloadType)['mappings'] | undefined;

    for (const from in mappings) {
      if (Object.prototype.hasOwnProperty.call(mappings, from)) {
        const to = mappings[from];

        if (nextPayload || !currentPayload || (from in currentPayload)) {
          if (!nextPayload) {
            nextPayload = {};
          }

          nextPayload[from] = to;
        } else {
          currentPayload[from] = to;
        }
      }
    }

    if (nextPayload) {
      this._mapKeys.push(nextPayload);
    }
  }

  /**
   * Marks items to be invalidated. All items will be invalidated before any items are resolved.
   */
  public invalidateItems(payload: IInvalidateItemsPayload): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    const {
      items
    } = payload;

    const currentPayload: IInvalidateItemsPayload['items'] | undefined = this._invalidateItems[this._invalidateItems.length - 1];

    let nextPayload: IInvalidateItemsPayload['items'] | undefined;

    for (const itemKey in items) {
      if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
        const facets = items[itemKey];

        if (nextPayload || !currentPayload || (itemKey in currentPayload)) {
          if (!nextPayload) {
            nextPayload = {};
          }

          nextPayload[itemKey] = facets;
        } else {
          currentPayload[itemKey] = facets;
        }
      }
    }

    if (nextPayload) {
      this._invalidateItems.push(nextPayload);
    }
  }

  /**
   * Provides resolutions for items. All resolutions will be put into the store in the correct order,
   * without conflict. Later resolutuons will take priority only if the same facet is resolved on the same items.
   */
  public resolveItems(payload: IResolveItemsPayload): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    const {
      items
    } = payload;

    const currentPayload: IResolveItemsPayload['items'] = this._resolveItems[this._resolveItems.length - 1];

    let nextPayload: IResolveItemsPayload['items'] | undefined;

    for (const itemKey in items) {
      if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
        const patch = items[itemKey];

        for (const facetId in patch) {
          if (Object.prototype.hasOwnProperty.call(patch, facetId)) {
            const facet = patch[facetId];

            if (nextPayload || !currentPayload || (currentPayload[itemKey] && (facetId in currentPayload[itemKey]))) {
              if (!nextPayload) {
                nextPayload = {};
              }

              if (!nextPayload[itemKey]) {
                nextPayload[itemKey] = {};
              }

              nextPayload[itemKey][facetId] = facet;
            } else {
              if (!currentPayload[itemKey]) {
                currentPayload[itemKey] = {};
              }

              currentPayload[itemKey][facetId] = facet;
            }
          }
        }
      }
    }

    if (nextPayload) {
      this._resolveItems.push(nextPayload);
    }
  }

  /**
   * Merges pending resolutions from a 'child' resolver.
   * All operations in the child will occur after those presently staged in the parent.
   * New operations added to the parent will occur after those in the child.
   */
  public merge(itemResolver: ItemResolver): void {
    itemResolver._done = true;

    this._mapKeys.push(...itemResolver._mapKeys);
    this._invalidateItems.push(...itemResolver._invalidateItems);
    this._resolveItems.push(...itemResolver._resolveItems);
  }

  /**
   * Creates an action which, when dispatched, executes the tasks in this ItemResolver in the correct sequence.
   */
  public execute(): IItemCacheThunkAction<void> {
    this._done = true;

    return (dispatch: IItemCacheDispatch) => {
      for (const payload of this._mapKeys) {
        dispatch(mapKeys({
          mappings: payload
        }));
      }

      for (const payload of this._invalidateItems) {
        dispatch(invalidateItems({
          items: payload
        }));
      }

      for (const payload of this._resolveItems) {
        dispatch(resolveItems({
          items: payload
        }));
      }
    };
  }
}
